import { useEffect, useState } from "react";

/**
 * Same as live (shopyourtrip.com main bundle useLocalStorage / a.Mj):
 * hydrate from localStorage, persist on every value change via useEffect.
 */
export function useLocalStorage(key, initialValue, notParse = false) {
  const prefixedKey = key;
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(prefixedKey);
      if (notParse) return stored;
      if (stored != null) return JSON.parse(stored);
      return typeof initialValue === "function" ? initialValue() : initialValue;
    } catch (error) {
      console.warn("localStorage not available (private mode?):", error);
      return typeof initialValue === "function" ? initialValue() : initialValue;
    }
  });

  useEffect(() => {
    try {
      const data = notParse ? value : JSON.stringify(value);
      localStorage.setItem(prefixedKey, data);
    } catch (error) {
      // If auth token cannot be written (quota), free hotel caches and retry.
      if (prefixedKey === "accessToken" && value != null) {
        try {
          const doomed = [];
          for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && k.startsWith("hotelDetCard:")) doomed.push(k);
          }
          doomed.forEach((k) => localStorage.removeItem(k));
          const data = notParse ? value : JSON.stringify(value);
          localStorage.setItem(prefixedKey, data);
          return;
        } catch {
          /* fall through */
        }
      }
      console.warn("localStorage setItem failed (private mode?):", error);
    }
  }, [prefixedKey, value, notParse]);

  return [value, setValue];
}

export function useSessionStorage(key, initialValue) {
  const prefixedKey = key;
  const [value, setValue] = useState(() => {
    try {
      const jsonValue = sessionStorage.getItem(prefixedKey);
      if (jsonValue != null) return JSON.parse(jsonValue);
      if (typeof initialValue === "function") {
        return initialValue();
      } else {
        return initialValue;
      }
    } catch (error) {
      console.warn("sessionStorage not available (private mode?):", error);
      if (typeof initialValue === "function") {
        return initialValue();
      } else {
        return initialValue;
      }
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(prefixedKey, JSON.stringify(value));
    } catch (error) {
      console.warn("sessionStorage setItem failed (private mode?):", error);
    }
  }, [prefixedKey, value]);

  return [value, setValue];
}

export function useLocalCartStorage(key, initialValue, notParse = false) {
  const prefixedKey = key;
  const [value, setValue] = useState(() => {
    try {
      const jsonValue = localStorage.getItem(prefixedKey);
      if (notParse) return jsonValue;
      if (jsonValue != null) return JSON.parse(jsonValue);
      if (typeof initialValue === "function") {
        return initialValue();
      } else {
        return initialValue;
      }
    } catch (error) {
      console.warn("localStorage not available (private mode?):", error);
      if (typeof initialValue === "function") {
        return initialValue();
      } else {
        return initialValue;
      }
    }
  });

  useEffect(() => {
    try {
      let data = "";
      if (notParse) {
        data = value;
      } else {
        data = JSON.stringify(value);
      }
      localStorage.setItem(prefixedKey, data);
    } catch (error) {
      console.warn("localStorage setItem failed (private mode?):", error);
    }
  }, [prefixedKey, value, notParse]);

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        if (
          value &&
          Object.keys(value).length > 0 &&
          value.hasOwnProperty("expiry")
        ) {
          const now = new Date().getTime();
          const expiry = value.expiry;
          if (expiry && now > expiry) {
            setValue(initialValue);
            localStorage.setItem(prefixedKey, JSON.stringify(initialValue));
          }
        }
      } catch (error) {
        console.warn("localStorage expiry check failed (private mode?):", error);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [prefixedKey, initialValue, value]);

  return [value, setValue];
}
